/**
 * One-shot playback: 32 permanent channel strips (filter -> gain -> pan), each routable to the
 * world bus or the UI bus. Playing a sound takes a free strip, or steals one by the policy in
 * `voicePolicy.ts`; the only node made per play is the buffer source WebAudio insists on.
 * Steals fade the victim over a few milliseconds and start the newcomer after the fade: no clicks.
 */

import type { Occlusion } from '../occlusion';
import { sfxDef, type SfxId } from '../sfx';
import { createSpatial, SPATIAL, spatialise } from '../spatial';
import { chooseSlot, MAX_VOICES, type VoiceSlot } from '../voicePolicy';
import type { Bank } from './bank';
import type { Graph } from './graph';

const STEAL_FADE = 0.008;

interface Strip extends VoiceSlot {
  filter: BiquadFilterNode;
  gain: GainNode;
  panner: StereoPannerNode | null;
  toWorld: GainNode;
  toUi: GainNode;
  source: AudioBufferSourceNode | null;
  endsAt: number;
}

export class Voices {
  private readonly strips: Strip[] = [];
  private readonly lastPlayed = new Map<SfxId, number>();
  private readonly spatial = createSpatial();
  private listenerX = 0;
  private listenerZ = 0;
  occlusion: Occlusion | null = null;

  constructor(private readonly graph: Graph, private readonly bank: Bank) {
    const ctx = graph.ctx;
    for (let i = 0; i < MAX_VOICES; i++) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.Q.value = 0.5;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const panner = typeof ctx.createStereoPanner === 'function' ? ctx.createStereoPanner() : null;
      const toWorld = ctx.createGain();
      const toUi = ctx.createGain();
      filter.connect(gain);
      const tail: AudioNode = panner ? gain.connect(panner) : gain;
      tail.connect(toWorld).connect(graph.world);
      tail.connect(toUi).connect(graph.ui);
      this.strips.push({
        filter, gain, panner, toWorld, toUi, source: null, endsAt: 0,
        busy: false, priority: 0, distance: 0, startedAt: 0,
      });
    }
  }

  setListener(x: number, z: number): void {
    this.listenerX = x;
    this.listenerZ = z;
  }

  /** A sound at a place in the world: panned, attenuated and low-passed by where the listener is. */
  playAt(id: SfxId, x: number, z: number, gainScale = 1, rate = 1): void {
    const def = sfxDef(id);
    const occluded = this.occlusion?.blocked(this.listenerX, this.listenerZ, x, z) ?? false;
    const range = def.maxDistance ?? SPATIAL.maxDistance;
    const s = spatialise(x - this.listenerX, z - this.listenerZ, range, occluded, this.spatial);
    if (s.gain <= 0.002) return;
    this.start(id, true, s.gain * gainScale, s.pan, s.cutoff, s.distance, rate);
  }

  /** A sound that is not in the world: a cue, a menu click. Flat, centred, unfiltered. */
  playUi(id: SfxId, gainScale = 1, rate = 1): void {
    this.start(id, false, gainScale, 0, SPATIAL.nearCutoff, 0, rate);
  }

  private start(
    id: SfxId,
    world: boolean,
    gainScale: number,
    pan: number,
    cutoff: number,
    distance: number,
    rate: number,
  ): void {
    const ctx = this.graph.ctx;
    // Before the first user gesture the clock is stopped: anything started now would pile up at
    // time zero and all go off at once when the context resumes.
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const def = sfxDef(id);
    const last = this.lastPlayed.get(id);
    if (last !== undefined && def.minInterval !== undefined && now - last < def.minInterval) return;

    for (const strip of this.strips) if (strip.busy && now >= strip.endsAt) strip.busy = false;
    const index = chooseSlot(this.strips, def.priority, distance);
    const strip = index >= 0 ? this.strips[index] : undefined;
    if (!strip) return;
    this.lastPlayed.set(id, now);

    let at = now;
    if (strip.busy && strip.source) {
      // Stolen: duck the old sound out, then begin. Eight milliseconds late is inaudible; a click is not.
      strip.gain.gain.cancelScheduledValues(now);
      strip.gain.gain.setValueAtTime(strip.gain.gain.value, now);
      strip.gain.gain.linearRampToValueAtTime(0, now + STEAL_FADE);
      strip.source.stop(now + STEAL_FADE);
      at = now + STEAL_FADE + 0.001;
    }

    const spread = def.rateSpread ?? 0;
    const playRate = rate * (1 + (Math.random() * 2 - 1) * spread);
    const source = ctx.createBufferSource();
    source.buffer = this.bank.next(id);
    source.playbackRate.value = playRate;
    source.connect(strip.filter);

    strip.filter.frequency.setValueAtTime(cutoff, at);
    strip.gain.gain.setValueAtTime(def.gain * gainScale, at);
    strip.panner?.pan.setValueAtTime(pan, at);
    strip.toWorld.gain.setValueAtTime(world ? 1 : 0, at);
    strip.toUi.gain.setValueAtTime(world ? 0 : 1, at);
    source.start(at);

    strip.source = source;
    strip.busy = true;
    strip.priority = def.priority;
    strip.distance = distance;
    strip.startedAt = at;
    strip.endsAt = at + (source.buffer?.duration ?? 0) / playRate + 0.02;
  }

  /** Fade everything out: the mission is over, or the tab is going away. */
  stopAll(): void {
    const now = this.graph.ctx.currentTime;
    for (const strip of this.strips) {
      if (!strip.busy || !strip.source) continue;
      strip.gain.gain.cancelScheduledValues(now);
      strip.gain.gain.setValueAtTime(strip.gain.gain.value, now);
      strip.gain.gain.linearRampToValueAtTime(0, now + 0.05);
      strip.source.stop(now + 0.06);
      strip.busy = false;
    }
  }

  /** Strips in use right now, for the debug overlay and the 32-voice acceptance check. */
  activeCount(): number {
    const now = this.graph.ctx.currentTime;
    let n = 0;
    for (const strip of this.strips) if (strip.busy && now < strip.endsAt) n++;
    return n;
  }
}
